import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Link, useNavigate } from 'react-router-dom';
import { getPlaylists, createPlaylist, deletePlaylist } from '../api';
import NavControls from '../components/NavControls';
import useSortable from '../hooks/useSortable';

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export default function Playlists() {
  const [search, setSearch] = useState('');
  const qc = useQueryClient();
  const navigate = useNavigate();

  const { data, isLoading } = useQuery('playlists', () => getPlaylists({ page_size: 1000 }).then(r => r.data));
  const playlists = data?.results || data || [];

  // Client-side search filter
  const filtered = search
    ? playlists.filter(p => p.name.toLowerCase().includes(search.toLowerCase()))
    : playlists;

  const { sorted, handleSort, SortIcon } = useSortable(filtered, 'name');

  const create = useMutation(
    (name) => createPlaylist({ name, is_smart: false }),
    { onSuccess: (res) => { qc.invalidateQueries('playlists'); navigate(`/playlists/${res.data.id}`); } }
  );

  const remove = useMutation(
    (id) => deletePlaylist(id),
    { onSuccess: () => qc.invalidateQueries('playlists') }
  );

  const handleNewManual = () => {
    const name = window.prompt('Playlist name:');
    if (name?.trim()) create.mutate(name.trim());
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <h1>Playlists</h1>
        <NavControls />
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 24, alignItems: 'center' }}>
        <input
          className="search-input"
          style={{ maxWidth: 300 }}
          placeholder="Search playlists…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {search && (
          <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>
            {filtered.length} result{filtered.length !== 1 ? 's' : ''}
          </span>
        )}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button className="btn btn-primary" onClick={() => navigate('/playlists/new/smart')}>✨ Smart Playlist</button>
        </div>
      </div>

      {isLoading ? <p style={{ color: 'var(--text-muted)' }}>Loading…</p> : (
        <table className="track-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('name')} style={{ cursor: 'pointer' }}>Name <SortIcon field="name" /></th>
              <th onClick={() => handleSort('track_count')} style={{ cursor: 'pointer' }}>Tracks <SortIcon field="track_count" /></th>
              <th onClick={() => handleSort('description')} style={{ cursor: 'pointer' }}>Description <SortIcon field="description" /></th>
              <th onClick={() => handleSort('created_at')} style={{ cursor: 'pointer' }}>Created <SortIcon field="created_at" /></th>
              <th onClick={() => handleSort('updated_at')} style={{ cursor: 'pointer' }}>Updated <SortIcon field="updated_at" /></th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {sorted.map(p => (
              <tr key={p.id}>
                <td><Link to={`/playlists/${p.id}`} style={{ color: 'var(--text)', textDecoration: 'none' }}>{p.name}</Link></td>
                <td style={{ color: 'var(--text-muted)' }}>{p.track_count}</td>
                <td style={{ color: 'var(--text-muted)' }}>{p.description || '—'}</td>
                <td style={{ color: 'var(--text-muted)' }}>{formatDate(p.created_at)}</td>
                <td style={{ color: 'var(--text-muted)' }}>{formatDate(p.updated_at)}</td>
                <td style={{ display: 'flex', gap: 6 }}>
                  {p.is_smart && (
                    <button className="btn btn-secondary" style={{ padding: '4px 10px', fontSize: 12 }}
                      onClick={() => navigate(`/playlists/${p.id}/edit`)}>Edit Rules</button>
                  )}
                  <button className="btn btn-secondary" style={{ padding: '4px 10px', fontSize: 12, color: '#f87171', borderColor: '#f87171' }}
                    onClick={() => window.confirm(`Delete "${p.name}"?`) && remove.mutate(p.id)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}